"use client";

import { useLocale, useTranslations } from "next-intl";
import { Link } from "@/i18n/navigation";
import { useCallback, useEffect, useRef, useState } from "react";
import { readMoreTextLinkClass } from "@/components/ReadMoreBlock";
import {
  contactFlowStepIds,
  intentOptionDefs,
  timelineOptionDefs,
  TOTAL_STEPS,
  type IntentId,
  type TimelineId,
} from "@/config/contact-flow";
import { organizationDetails } from "@/config/site";
import type { AppLocale } from "@/i18n/routing";

const RECAPTCHA_SITE_KEY = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY;

const textLinkButtonClass = `${readMoreTextLinkClass} bg-transparent border-0 p-0 cursor-pointer text-left disabled:opacity-40 disabled:cursor-not-allowed`;

const inputClassName =
  "w-full bg-transparent border-b border-[var(--outline)] py-3 text-[var(--on-surface)] placeholder:text-[var(--on-surface-variant)]/60 focus:outline-none focus:border-[var(--primary)] focus:bg-[var(--surface-container-high)] transition-colors duration-200";

const primarySubmitClass =
  "bg-[var(--primary)] text-[var(--on-primary)] px-6 sm:px-8 py-3.5 sm:py-4 rounded-lg font-medium tracking-wide hover:opacity-90 transition-all disabled:opacity-70 touch-manipulation focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]";

const landingCardClass =
  "bg-[var(--surface)] p-6 sm:p-8 md:p-10 hover:bg-[var(--primary-container)] transition-colors text-left focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[var(--primary)]";

export function ContactGuidedFlow() {
  const locale = useLocale() as AppLocale;
  const t = useTranslations("contact.labels");
  const tSteps = useTranslations("contact.steps");

  const [step, setStep] = useState(0);

  const [intentId, setIntentId] = useState<IntentId | null>(null);
  const [timelineId, setTimelineId] = useState<TimelineId | null>(null);
  const [needs, setNeeds] = useState("");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [consentAccepted, setConsentAccepted] = useState(false);

  const [localError, setLocalError] = useState("");
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">(
    "idle",
  );
  const [errorMessage, setErrorMessage] = useState("");

  const stepTitleRef = useRef<HTMLHeadingElement>(null);
  const successTitleRef = useRef<HTMLHeadingElement>(null);
  const prevStep = useRef<number | null>(null);

  useEffect(() => {
    if (status === "success") {
      successTitleRef.current?.focus({ preventScroll: true });
      return;
    }
    if (prevStep.current === null) {
      prevStep.current = step;
      return;
    }
    if (prevStep.current === step) return;
    prevStep.current = step;
    stepTitleRef.current?.focus({ preventScroll: true });
  }, [step, status]);

  const pickIntent = useCallback((id: IntentId) => {
    setLocalError("");
    setIntentId(id);
    setStep(1);
  }, []);

  const pickTimeline = useCallback((id: TimelineId) => {
    setLocalError("");
    setTimelineId(id);
    setStep(2);
  }, []);

  const goBack = useCallback(() => {
    setLocalError("");
    setErrorMessage("");
    setStep((s) => Math.max(s - 1, 0));
  }, []);

  const continueFromNeeds = useCallback(() => {
    setLocalError("");
    if (!needs.trim()) {
      setLocalError(t("validationNeeds"));
      return;
    }
    setStep(3);
  }, [needs, t]);

  const continueFromContact = useCallback(() => {
    setLocalError("");
    if (!name.trim() || !email.trim()) {
      setLocalError(t("validationNameEmail"));
      return;
    }
    setStep(4);
  }, [name, email, t]);

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setLocalError("");
      setErrorMessage("");
      if (!intentId || !timelineId) return;
      if (!consentAccepted) {
        setLocalError(t("validationConsent"));
        return;
      }

      setStatus("submitting");

      let recaptchaToken = "";
      if (RECAPTCHA_SITE_KEY && typeof window !== "undefined") {
        const w = window as unknown as {
          grecaptcha?: {
            ready: (fn: () => void) => void;
            execute: (key: string, opts: { action: string }) => Promise<string>;
          };
        };
        if (w.grecaptcha) {
          await new Promise<void>((resolve) => {
            w.grecaptcha!.ready(() => resolve());
          });
          recaptchaToken = await w.grecaptcha.execute(RECAPTCHA_SITE_KEY, {
            action: "contact",
          });
        }
      }

      const payload = {
        intentId,
        timelineId,
        needs: needs.trim(),
        name: name.trim(),
        email: email.trim(),
        company: company.trim() || undefined,
        consentAccepted: true as const,
        recaptchaToken,
        locale,
      };

      try {
        const res = await fetch("/api/contact", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = (await res.json().catch(() => ({}))) as { error?: string };

        if (!res.ok) {
          setStatus("error");
          setErrorMessage(data.error ?? t("error"));
          return;
        }
        setStatus("success");
      } catch {
        setStatus("error");
        setErrorMessage(t("error"));
      }
    },
    [intentId, timelineId, needs, name, email, company, consentAccepted, locale, t],
  );

  if (status === "success") {
    return (
      <div className="max-w-xl">
        <h2
          ref={successTitleRef}
          tabIndex={-1}
          className="font-display text-2xl sm:text-3xl text-[var(--on-surface)] mb-4 tracking-tight outline-none"
          style={{ fontFamily: "var(--font-display)" }}
        >
          {t("successTitle")}
        </h2>
        <p
          className="text-[var(--on-surface-variant)] leading-relaxed mb-10 text-base sm:text-lg"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {t("successBody")}
        </p>
        <p
          className="text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[var(--on-surface-variant)]/60 mb-4 font-semibold"
          style={{ fontFamily: "var(--font-body)" }}
        >
          <DirectLinesLabel />
        </p>
        <ContactSuccessDirect />
      </div>
    );
  }

  const stepId = contactFlowStepIds[step];
  const stepTitle = tSteps(stepId);

  return (
    <div className="max-w-2xl">
      <p
        className="text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[var(--on-surface-variant)]/60 mb-6 sm:mb-8 font-semibold"
        style={{ fontFamily: "var(--font-body)" }}
        aria-live="polite"
      >
        {t("stepIndicator", { n: step + 1, total: TOTAL_STEPS })}
      </p>

      <form onSubmit={step === TOTAL_STEPS - 1 ? handleSubmit : (e) => e.preventDefault()}>
        <fieldset key={step} className="border-0 p-0 m-0 min-w-0 contact-flow-step-animate">
          <legend className="sr-only">{stepTitle}</legend>
          <h2
            ref={stepTitleRef}
            id={`contact-flow-step-${step}`}
            tabIndex={-1}
            className="font-display text-2xl sm:text-3xl text-[var(--on-surface)] mb-6 sm:mb-8 tracking-tight outline-none"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {stepTitle}
          </h2>

          {step === 0 && <IntentStep onPick={pickIntent} />}
          {step === 1 && <TimelineStep onPick={pickTimeline} />}
          {step === 2 && intentId && (
            <NeedsStep
              needs={needs}
              onNeedsChange={setNeeds}
              intentId={intentId}
            />
          )}
          {step === 3 && (
            <ContactFieldsStep
              name={name}
              email={email}
              company={company}
              onNameChange={setName}
              onEmailChange={setEmail}
              onCompanyChange={setCompany}
              disabled={status === "submitting"}
            />
          )}
          {step === 4 && intentId && timelineId && (
            <ConsentStep
              intentId={intentId}
              timelineId={timelineId}
              needs={needs}
              name={name}
              email={email}
              company={company}
              consentAccepted={consentAccepted}
              onConsentChange={setConsentAccepted}
              disabled={status === "submitting"}
            />
          )}
        </fieldset>

        <div
          className="mt-10 sm:mt-12 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between"
          role="status"
          aria-live="polite"
        >
          <div className="order-2 sm:order-1 sm:self-end">
            {step > 0 && (
              <button
                type="button"
                onClick={goBack}
                disabled={status === "submitting"}
                className={textLinkButtonClass}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("goBack")}
              </button>
            )}
          </div>

          <div className="order-1 sm:order-2 flex flex-col items-stretch sm:items-end gap-3 w-full sm:w-auto">
            {localError && (
              <p className="text-sm text-[var(--error)] text-right" style={{ fontFamily: "var(--font-body)" }}>
                {localError}
              </p>
            )}
            {status === "error" && errorMessage && (
              <p className="text-sm text-[var(--error)] text-right" style={{ fontFamily: "var(--font-body)" }}>
                {errorMessage}
              </p>
            )}
            {step === 2 && (
              <button
                type="button"
                onClick={continueFromNeeds}
                disabled={status === "submitting"}
                className={textLinkButtonClass}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("nextToDetails")}
              </button>
            )}
            {step === 3 && (
              <button
                type="button"
                onClick={continueFromContact}
                disabled={status === "submitting"}
                className={textLinkButtonClass}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {t("nextToReview")}
              </button>
            )}
            {step === TOTAL_STEPS - 1 && (
              <button
                type="submit"
                disabled={status === "submitting"}
                className={primarySubmitClass}
                style={{ fontFamily: "var(--font-body)" }}
              >
                {status === "submitting" ? t("submitting") : t("submit")}
              </button>
            )}
          </div>
        </div>
      </form>
    </div>
  );
}

function DirectLinesLabel() {
  const t = useTranslations("contact");
  return <>{t("directLines")}</>;
}

function ContactSuccessDirect() {
  return (
    <address
      className="not-italic space-y-3 text-[var(--on-surface)]"
      style={{ fontFamily: "var(--font-body)" }}
    >
      <p>
        <Link href={organizationDetails.phoneTelHref} className={readMoreTextLinkClass}>
          {organizationDetails.phoneDisplay}
        </Link>
      </p>
      <p>
        <Link
          href={`mailto:${organizationDetails.email}`}
          className={`${readMoreTextLinkClass} break-all`}
        >
          {organizationDetails.email}
        </Link>
      </p>
    </address>
  );
}

function IntentStep({ onPick }: { onPick: (id: IntentId) => void }) {
  const t = useTranslations("contact");
  const tIntent = useTranslations("contact.intent");
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-[var(--outline)]" role="group" aria-label={t("topicAria")}>
      {intentOptionDefs.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onPick(opt.id)}
          className={landingCardClass}
        >
          <span
            className="font-display text-xl sm:text-2xl mb-3 text-[var(--on-surface)] tracking-tight block"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {tIntent(`${opt.id}.label`)}
          </span>
          <span
            className="text-[var(--on-surface-variant)] leading-relaxed text-sm sm:text-base"
            style={{ fontFamily: "var(--font-body)" }}
          >
            {tIntent(`${opt.id}.shortLabel`)}
          </span>
        </button>
      ))}
    </div>
  );
}

function TimelineStep({ onPick }: { onPick: (id: TimelineId) => void }) {
  const t = useTranslations("contact");
  const tTimelines = useTranslations("contact.timelines");
  return (
    <div className="grid grid-cols-1 gap-px bg-[var(--outline)]" role="group" aria-label={t("timelineAria")}>
      {timelineOptionDefs.map((opt) => (
        <button
          key={opt.id}
          type="button"
          onClick={() => onPick(opt.id)}
          className={`${landingCardClass} w-full`}
        >
          <span
            className="font-display text-xl sm:text-2xl mb-2 text-[var(--on-surface)] tracking-tight block"
            style={{ fontFamily: "var(--font-display)" }}
          >
            {tTimelines(`${opt.id}.label`)}
          </span>
          <span
            className="text-[var(--on-surface-variant)] leading-relaxed text-sm sm:text-base"
            style={{ fontFamily: "var(--font-body)" }}
          >
            {tTimelines(`${opt.id}.description`)}
          </span>
        </button>
      ))}
    </div>
  );
}

function NeedsStep({
  needs,
  onNeedsChange,
  intentId,
}: {
  needs: string;
  onNeedsChange: (v: string) => void;
  intentId: IntentId;
}) {
  const t = useTranslations("contact.labels");
  const tContact = useTranslations("contact");
  const tHintsRoot = useTranslations("contact.hints");
  const tIntent = useTranslations("contact.intent");
  const opt = intentOptionDefs.find((o) => o.id === intentId)!;
  const bullets = tHintsRoot.raw(`${intentId}.bullets`) as string[];

  return (
    <div>
      <p
        className="text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[var(--on-surface-variant)]/80 mb-3 font-semibold"
        style={{ fontFamily: "var(--font-body)" }}
      >
        {tHintsRoot(`${intentId}.title`)}
      </p>
      <ul
        className="list-disc pl-5 space-y-2 text-sm text-[var(--on-surface-variant)] mb-6"
        style={{ fontFamily: "var(--font-body)" }}
      >
        {bullets.map((b) => (
          <li key={b}>{b}</li>
        ))}
      </ul>
      <label htmlFor="contact-needs" className="sr-only">
        {tContact("needsLabelSr")}
      </label>
      <textarea
        id="contact-needs"
        value={needs}
        onChange={(e) => onNeedsChange(e.target.value)}
        rows={6}
        className={`${inputClassName} resize-none border-[var(--outline-variant)]`}
        style={{ fontFamily: "var(--font-body)" }}
        placeholder={t("needsPlaceholder")}
      />
      <p className="mt-4 flex flex-wrap items-baseline gap-x-3 gap-y-1 text-sm" style={{ fontFamily: "var(--font-body)" }}>
        <Link href={opt.readMoreHref} className={readMoreTextLinkClass}>
          {tIntent(`${intentId}.readMoreLabel`)}
        </Link>
        <span className="text-[var(--on-surface-variant)]">{tContact("scopeHint")}</span>
      </p>
    </div>
  );
}

function ContactFieldsStep({
  name,
  email,
  company,
  onNameChange,
  onEmailChange,
  onCompanyChange,
  disabled,
}: {
  name: string;
  email: string;
  company: string;
  onNameChange: (v: string) => void;
  onEmailChange: (v: string) => void;
  onCompanyChange: (v: string) => void;
  disabled: boolean;
}) {
  const t = useTranslations("contact.labels");
  const tPh = useTranslations("contact.placeholders");
  return (
    <div className="space-y-6 max-w-xl">
      <div>
        <label
          htmlFor="contact-name"
          className="block text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[var(--on-surface-variant)]/80 mb-2 font-semibold"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {t("name")}
        </label>
        <input
          id="contact-name"
          type="text"
          value={name}
          onChange={(e) => onNameChange(e.target.value)}
          disabled={disabled}
          autoComplete="name"
          className={inputClassName}
          style={{ fontFamily: "var(--font-body)" }}
          placeholder={tPh("name")}
        />
      </div>
      <div>
        <label
          htmlFor="contact-email"
          className="block text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[var(--on-surface-variant)]/80 mb-2 font-semibold"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {t("email")}
        </label>
        <input
          id="contact-email"
          type="email"
          value={email}
          onChange={(e) => onEmailChange(e.target.value)}
          disabled={disabled}
          autoComplete="email"
          className={inputClassName}
          style={{ fontFamily: "var(--font-body)" }}
          placeholder={tPh("email")}
        />
      </div>
      <div>
        <label
          htmlFor="contact-company"
          className="block text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[var(--on-surface-variant)]/80 mb-2 font-semibold"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {t("company")}
        </label>
        <input
          id="contact-company"
          type="text"
          value={company}
          onChange={(e) => onCompanyChange(e.target.value)}
          disabled={disabled}
          autoComplete="organization"
          className={inputClassName}
          style={{ fontFamily: "var(--font-body)" }}
          placeholder={tPh("company")}
        />
      </div>
    </div>
  );
}

function ConsentStep({
  intentId,
  timelineId,
  needs,
  name,
  email,
  company,
  consentAccepted,
  onConsentChange,
  disabled,
}: {
  intentId: IntentId;
  timelineId: TimelineId;
  needs: string;
  name: string;
  email: string;
  company: string;
  consentAccepted: boolean;
  onConsentChange: (v: boolean) => void;
  disabled: boolean;
}) {
  const t = useTranslations("contact.labels");
  const tContact = useTranslations("contact");
  const tIntent = useTranslations("contact.intent");
  const tTimelines = useTranslations("contact.timelines");

  return (
    <div className="space-y-8 max-w-xl">
      <div
        className="rounded-lg bg-[var(--surface)] p-5 sm:p-6 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--on-surface)_8%,transparent)]"
        aria-labelledby="contact-summary-heading"
      >
        <h3
          id="contact-summary-heading"
          className="text-[var(--label-md)] uppercase tracking-[0.12em] sm:tracking-[0.2em] text-[var(--primary)] mb-4 font-semibold"
          style={{ fontFamily: "var(--font-body)" }}
        >
          {t("summaryTitle")}
        </h3>
        <dl className="space-y-3 text-sm" style={{ fontFamily: "var(--font-body)" }}>
          <div className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="text-[var(--on-surface-variant)] shrink-0 sm:w-28">{tContact("summaryTopic")}</dt>
            <dd className="text-[var(--on-surface)]">{tIntent(`${intentId}.label`)}</dd>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="text-[var(--on-surface-variant)] shrink-0 sm:w-28">{tContact("summaryTimeline")}</dt>
            <dd className="text-[var(--on-surface)]">{tTimelines(`${timelineId}.label`)}</dd>
          </div>
          <div className="flex flex-col gap-1">
            <dt className="text-[var(--on-surface-variant)]">{tContact("summaryNeeds")}</dt>
            <dd className="text-[var(--on-surface)] whitespace-pre-wrap">{needs}</dd>
          </div>
          <div className="flex flex-col sm:flex-row sm:gap-4">
            <dt className="text-[var(--on-surface-variant)] shrink-0 sm:w-28">{tContact("summaryFrom")}</dt>
            <dd className="text-[var(--on-surface)]">
              {name} · {email}
              {company ? ` · ${company}` : ""}
            </dd>
          </div>
        </dl>
      </div>

      <div>
        <label htmlFor="contact-consent" className="flex gap-3 items-start cursor-pointer group">
          <input
            id="contact-consent"
            type="checkbox"
            checked={consentAccepted}
            onChange={(e) => onConsentChange(e.target.checked)}
            disabled={disabled}
            className="mt-1 size-4 rounded-sm border border-[var(--outline-variant)] bg-transparent text-[var(--primary)] focus:ring-1 focus:ring-[var(--primary)]"
          />
          <span className="text-sm text-[var(--on-surface-variant)] leading-relaxed" style={{ fontFamily: "var(--font-body)" }}>
            {t("consentLabel")}{" "}
            <Link href="/privacy" className={readMoreTextLinkClass}>
              {t("consentPrivacyLink")}
            </Link>
            .
          </span>
        </label>
      </div>
    </div>
  );
}
